<template>
  <div>
    <!-- Checkbox to toggle between all events and future events -->
    <div class="flex items-center mb-4">
      <input
        type="checkbox"
        id="showAllEvents"
        v-model="showAllEvents"
        class="mr-2"
      />
      <label for="showAllEvents" class="text-sm text-neutral-700">
        Inkluder tidligere fridage
      </label>
    </div>

    <!-- Message when there are no events -->
    <div
      v-if="groupedEvents.length === 0"
      class="flex items-center justify-center text-sm text-center text-neutral-400"
    >
      {{ showAllEvents ? 'Der er ingen registrerede fridage.' : 'Du har ikke registreret nogen fremtidige fridage endnu.' }}
    </div>
    <!-- List of future events grouped by month -->
    <div v-else class="flex flex-col gap-1.5 -m-2.5">
      <div
        v-for="(month, monthIndex) in groupedEvents"
        :key="monthIndex"
        class="flex flex-col gap-1.5"
      >
        <!-- Month label -->
        <h3 class="text-md font-semibold text-neutral-800 pl-2.5 mt-2">
          {{ month.monthLabel }}
        </h3>
        <!-- Event groups for the month -->
        <div
          v-for="eventGroup in month.events"
          :key="eventGroup.batch_id"
          class="relative rounded-lg p-2.5"
          :class="[ 
            getEventTypeColors(eventGroup.type).border,
            getEventTypeColors(eventGroup.type).text,
            getEventTypeColors(eventGroup.type).bg,
            'border-solid border',
            authStore.isAdmin ? 'group cursor-pointer hover:bg-white hover:border-red-500 hover:text-red-700' : '' ]"
          @click="authStore.isAdmin && openModal(eventGroup)"
        >
          <div
            :class="{'group-hover:opacity-40': authStore.isAdmin}"
          >
            <div class="flex flex-col items-start pl-0.5">
              <!-- Event type, date range, and replacement -->
              <div class="text-sm font-semibold truncate">
                {{ eventGroup.type }}
              </div>
              <div class="text-sm truncate">
                {{ eventGroup.dateRange }}
              </div>
            </div>
          </div>
          <!-- Admin-only overlay with delete icon or lock icon -->
          <div
            v-if="authStore.isAdmin"
            class="absolute inset-0 flex items-center justify-center rounded-lg opacity-0 bg-opacity-60 group-hover:opacity-100"
            :class="[!authStore.isAdmin && eventGroup.type === 'Sygdom' ? 'bg-neutral-100' : 'bg-red-100']"
          >
            <Lock v-if="!authStore.isAdmin && eventGroup.type === 'Sygdom'" :size="24" class="text-neutral-700" />
            <Trash2 v-else :size="24" class="text-red-700" />
          </div>
        </div>
      </div>
    </div>
    <!-- Modal for confirming event group deletion -->
    <ReusableModal
      :isOpen="showModal"
      :isConfirm="false"
      @close="closeModal"
      @submit="handleDeleteEventGroup"
      title="Er du sikker på, at du vil slette denne fridag?"
    >
      <p class="text-sm text-neutral-800">
        Hvis dette er en fridag med saldo, så vil du få en refundering.
      </p>
    </ReusableModal>
  </div>
</template>

<script setup>
import { computed, defineProps, ref } from "vue";
import { useEventsStore } from "@/stores/eventsStore";
import { Trash2, Lock } from "lucide-vue-next";
import { useAuthStore } from "@/stores/authStore";
import { useUsersStore } from "@/stores/usersStore";
import ReusableModal from "@/components/ui/ReusableModal.vue";

// Initialize stores
const authStore = useAuthStore();
const eventsStore = useEventsStore();
const usersStore = useUsersStore();

// Define props
const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
});

// Reactive state
const showModal = ref(false);
const showAllEvents = ref(false);
const activeEventGroup = ref(null);

// Computed property to group future events by month
const groupedEvents = computed(() => {
  const now = new Date();
  const filtered = showAllEvents.value
    ? props.user.events
    : props.user.events.filter((event) => {
        const eventDate = new Date(event.date);
        return eventDate > now;
      });

  const grouped = filtered.reduce((acc, event) => {
    const eventDate = new Date(event.date);
    const monthYear = `${eventDate.getFullYear()}-${eventDate.getMonth()}`;

    if (!acc[monthYear]) {
      acc[monthYear] = {
        monthLabel: formatMonthYear(eventDate),
        events: [],
      };
    }

    const existingGroup = acc[monthYear].events.find(
      (group) => group.batch_id === event.batch_id
    );
    if (existingGroup) {
      existingGroup.dates.push(eventDate);
      existingGroup.events.push(event);
    } else {
      acc[monthYear].events.push({
        type: event.type,
        dates: [eventDate],
        events: [event],
        batch_id: event.batch_id,
      });
    }

    return acc;
  }, {});

  // Sort months and format date ranges
  return Object.values(grouped)
    .sort(
      (a, b) => new Date(a.events[0].dates[0]) - new Date(b.events[0].dates[0])
    )
    .map((month) => ({
      ...month,
      events: month.events.map((group) => ({
        ...group,
        dateRange: formatDateRange(group.dates),
      })),
    }));
});

/**
 * Format month and year as a string and capitalize the first letter.
 * @param {Date} date - The date to format.
 * @returns {String} - The formatted month and year.
 */
function formatMonthYear(date) {
  const monthYear = date.toLocaleString("da-DK", {
    month: "long",
    year: "numeric",
  });
  return monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
}

/**
 * Get the color classes for a given event type.
 * @param {String} eventType - The event type.
 * @returns {Object} - The color classes for the event type.
 */
const getEventTypeColors = (eventType) => {
  switch (eventType) {
    case "Samtidighedsferieuger":
      return {
        border: "border-green-500 border-solid border",
        text: "text-green-800",
        bg: "bg-green-50",
      };
    case "Særlige feriedage":
      return {
        border: "border-purple-500 border-solid border",
        text: "text-purple-800",
        bg: "bg-purple-50",
      };
    case "Søgnedage":
      return {
        border: "border-amber-500 border-solid border",
        text: "text-amber-800",
        bg: "bg-amber-50",
      };
    case "Friweekender":
      return {
        border: "border-pink-500 border-solid border",
        text: "text-pink-800",
        bg: "bg-pink-50",
      };
    case "Forlagt tjeneste":
      return {
        border: "border-teal-500 border-solid border",
        text: "text-teal-800",
        bg: "bg-teal-50",
      };
    case "Kursusvirksomhed":
      return {
        border: "border-lime-600 border-solid border",
        text: "text-lime-900",
        bg: "bg-lime-50",
      };
    case "Erstatningsfridag":
      return {
        border: "border-gray-500 border-solid border",
        text: "text-gray-800",
        bg: "bg-gray-100",
      };
    case "Sygdom":
      return {
        border: "border-red-500 border-solid border",
        text: "text-red-800",
        bg: "bg-red-50",
      };
    default:
      return {
        border: "border-neutral-500 border-solid border",
        text: "text-neutral-800",
        bg: "bg-neutral-100",
      };
  }
};

/**
 * Handle the deletion of an event group.
 */
async function handleDeleteEventGroup() {
  showModal.value = false;
  if (!authStore.isAdmin && activeEventGroup.value.type === "Sygdom") {
    return;
  }
  await eventsStore.removeEvent(activeEventGroup.value.events[0]);
  if (authStore.isAdmin) {
    await usersStore.fetchUsers();
  } else {
    await authStore.fetchLoggedInUser();
  }
}

/**
 * Format a range of dates as a string.
 * @param {Array} dates - The dates to format.
 * @returns {String} - The formatted date range.
 */
function formatDateRange(dates) {
  const sortedDates = dates.sort((a, b) => a - b);
  const firstDate = formatDate(sortedDates[0]);
  const lastDate = formatDate(sortedDates[sortedDates.length - 1]);
  return sortedDates.length > 1 ? `${firstDate} - ${lastDate}` : firstDate;
}

/**
 * Format a date as a string.
 * @param {Date} date - The date to format.
 * @returns {String} - The formatted date.
 */
function formatDate(date) {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }

  if (isNaN(date.getTime())) {
    console.error("Invalid date:", date);
    return "Invalid Date";
  }

  try {
    return date.toLocaleDateString("da-DK", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Date formatting error";
  }
}

/**
 * Open the modal to confirm event group deletion.
 * @param {Object} eventGroup - The event group to delete.
 */
function openModal(eventGroup) {
  if(authStore.isAdmin) {
  if (!authStore.isAdmin && eventGroup.type === 'Sygdom') {
    return;
  }
  activeEventGroup.value = eventGroup;
  showModal.value = true;
  }
}

/**
 * Close the modal.
 */
function closeModal() {
  showModal.value = false;
}
</script>